/**
 * Hook to sync favorite sessions with database
 */

import type { ChatSessionModel } from "@/api/models"
import type { ModuleName } from "@/types/user-settings"

import { useFavoriteSessions } from "@/hooks/use-module-settings"
import { useToggleFavoriteSession } from "@/hooks/use-user-settings"
import { KNOWN_SESSION_TYPES } from "@/types"

export function useFavoriteSessionsSync(sessionType: string) {
  const { favorites, isLoading } = useFavoriteSessions()
  const { mutate: toggleFavorite } = useToggleFavoriteSession()

  // Map session type to module name
  const getModuleName = (type: string): ModuleName => {
    switch (type) {
      case KNOWN_SESSION_TYPES.Explorer:
        return "ai_explorer"
      case KNOWN_SESSION_TYPES.Translator:
        return "ai_translator"
      case KNOWN_SESSION_TYPES.VideoInsight:
        return "video_insight"
      default:
        return "ai_explorer"
    }
  }

  const moduleName = getModuleName(sessionType)
  const favoriteSessionIds = favorites?.[moduleName] || []

  const toggleFavoriteSession = (session: ChatSessionModel) => {
    if (!session?.id) return
    toggleFavorite({ module: moduleName, sessionId: session.id })
  }

  const isFavorited = (sessionId: string | undefined): boolean => {
    if (!sessionId) return false
    return favoriteSessionIds.includes(sessionId)
  }

  // Get favorite sessions from the list
  const getFavoriteSessions = (sessions: ChatSessionModel[]): ChatSessionModel[] => {
    return sessions.filter((session) => session?.id && favoriteSessionIds.includes(session.id))
  }

  return {
    favoriteSessionIds,
    toggleFavoriteSession,
    isFavorited,
    getFavoriteSessions,
    isLoading,
  }
}
